import React from 'react';
import { CampaignStatus, type CampaignAutoPauseResponse } from '@troia-v3/types';
import { StatusPill } from '@/shared/components/ui/StatusPill';
import type { StatusPillVariant } from '@/shared/components/ui/StatusPill';

interface CampaignStatusBadgeProps {
  status: CampaignStatus;
  /** Presente quando a campanha se pausou sozinha (canal fora do ar). */
  autoPause?: CampaignAutoPauseResponse;
  'data-testid'?: string;
}

/** Rótulo pt-BR + semântica de cor por status de campanha (F62/F64/F65):
 *  verde = concluída · azul = em andamento/agendada · âmbar/erro conforme falha. */
const STATUS_META: Record<string, { label: string; variant: StatusPillVariant }> = {
  draft: { label: 'Rascunho', variant: 'neutral' },
  scheduled: { label: 'Agendada', variant: 'info' },
  in_progress: { label: 'Em progresso', variant: 'info' },
  paused: { label: 'Pausada', variant: 'neutral' },
  completed: { label: 'Concluída', variant: 'success' },
  failed: { label: 'Falhou', variant: 'error' },
  cancelled: { label: 'Cancelada', variant: 'error' },
  archived: { label: 'Arquivada', variant: 'neutral' },
};

/** Texto do motivo da pausa automática (tooltip do selo). */
export function campaignAutoPauseReasonText(autoPause: CampaignAutoPauseResponse): string {
  const canal = autoPause.details.channelName ?? autoPause.details.channelId ?? 'usado por ela';
  if (autoPause.reason === 'channel_missing') {
    return `Pausada automaticamente: o canal ${canal} não existe mais. Escolha outro canal e retome.`;
  }
  if (autoPause.reason === 'rate_limit_horizon') {
    return `Pausada automaticamente: o público é grande demais para o ritmo de envio do canal ${canal} (passaria de 30 dias). Aumente a velocidade do canal ou divida a campanha e retome.`;
  }
  if (autoPause.reason === 'insufficient_credits') {
    const { pendingMessages, creditsNeeded } = autoPause.details;
    const quantidade = pendingMessages !== undefined ? `as ${pendingMessages} mensagens que ainda faltavam enviar` : 'as mensagens que ainda faltavam enviar';
    const custo = creditsNeeded !== undefined ? ` (custo estimado: ${creditsNeeded} créditos)` : '';
    return `Pausada automaticamente: faltou crédito para ${quantidade}${custo}. Recarregue os créditos e retome.`;
  }
  return `Pausada automaticamente: o canal ${canal} está desconectado. Reconecte o canal e retome.`;
}

/**
 * Campaign Status Badge — wrapper fino do StatusPill shared (decisão F62:
 * pill SEM borda, 23px, dot currentColor; fim do markup manual por módulo).
 *
 * Quando `status === 'paused'` e a campanha tem `autoPause` (pausa automática
 * por canal fora do ar, 2026-08-27), o selo vira "Pausada automaticamente"
 * (variant warning) com o motivo no `title` — mesma solução do
 * `WorkflowStatusBadge`. Sem isso o dono vê só "Pausada" e não sabe se foi ele
 * ou o sistema.
 *
 * @example
 * <CampaignStatusBadge status={campaign.status} autoPause={campaign.autoPause} />
 */
export const CampaignStatusBadge: React.FC<CampaignStatusBadgeProps> = ({
  status,
  autoPause,
  'data-testid': dataTestId,
}) => {
  const meta = STATUS_META[status] ?? STATUS_META.draft;
  const auto = status === CampaignStatus.PAUSED && autoPause !== undefined;

  if (!auto) {
    return <StatusPill variant={meta.variant} label={meta.label} data-testid={dataTestId} />;
  }

  return (
    <span title={campaignAutoPauseReasonText(autoPause)} data-auto-pause={autoPause.reason}>
      <StatusPill variant="warning" label="Pausada automaticamente" data-testid={dataTestId} />
    </span>
  );
};

CampaignStatusBadge.displayName = 'CampaignStatusBadge';
